<template>
    <div>
        <v-dialog
            v-model="isOpen"
            max-width="1000"
            :scrollable="false"
            @keydown.esc.prevent="closeModal"
            persistent
            @click:outside="closeModal"
        >
            <v-card class="pa-0 modalOverflowHidden" v-if="taskId" ref="taskInfoCard">
                <v-toolbar color="grey lighten-4 px-3" flat>
                    <v-toolbar-title class="text-uppercase">
                        <v-icon v-if="taskInfo.overdue" class="mr-1">$overdue</v-icon>
                        {{taskInfo.title}}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeModal">
                        <v-icon>$close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-fade-transition>
                    <v-container fluid class="pa-5 task-view_container" v-if="!isDataLoading">
                        <v-form ref="taskEditForm" class="task-view_form">
                            <v-layout row px-4 fluid class="task-view_content"
                                :style="{height: scrollableContentHeight}">
                                <v-flex xs7 pr-5 border-right-separator class="overflowForFixedCol">
                                    <div v-if="showEvidenceBlock">
                                        <h3>Evidence required to complete</h3>
                                        <v-layout row px-4 mt-5 mb-5 v-if="showRenewableDate">
                                            <v-flex xs4 class="d-flex">
                                                <v-subheader class="subtitle-1 required">
                                                    Renew date
                                                </v-subheader>
                                            </v-flex>
                                            <v-flex xs8 class="align-center">
                                                <v-menu
                                                    v-model="datePickerStatus"
                                                    :close-on-content-click="false"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-text-field
                                                            :value="computedRenewDateFormatted"
                                                            :rules="renewableDateRules"
                                                            :disabled="taskInfo.status !== 'Pending'"
                                                            label="Select date"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            v-on="on"
                                                            v-bind="attrs"
                                                        ></v-text-field>
                                                    </template>
                                                    <v-date-picker first-day-of-week="1"
                                                        v-model="userRenewableTaskDueDate"
                                                        @input="datePickerStatus = false"
                                                    />
                                                </v-menu>
                                                <div class="mt2">
                                                    Actual date: {{Date.formatDate([renewableTaskDueDate])}}
                                                </div>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row px-4 mt-5 mb-5 v-if="isHrRenewable">
                                            <v-flex xs4 class="d-flex align-center">
                                                <v-subheader class="subtitle-1">
                                                    Supervisor role
                                                </v-subheader>
                                            </v-flex>
                                            <v-flex xs8 class="d-flex align-center">
                                                <v-select
                                                    :items="$store.getters.getVisibleRoles"
                                                    item-text="title"
                                                    item-value="id"
                                                    placeholder="Role"
                                                    v-model="taskInfo.supervisorIds"
                                                    dense
                                                    solo
                                                    lighten-1
                                                    multiple
                                                ></v-select>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row px-4 mt-5 mb-5 v-if="taskInfo.imageObligatoriness !== 'Disabled'">
                                            <v-flex xs4 class="d-flex">
                                                <v-subheader class="subtitle-1"
                                                    :class="taskInfo.imageObligatoriness === 'Mandatory' ? 'required' : ''">
                                                    Upload files
                                                </v-subheader>
                                            </v-flex>
                                            <v-flex xs8 class="align-center">
                                                <ul class="pa-0 mb-4" v-if="uploadedImages.length > 0">
                                                    <li class="d-flex align-center"
                                                        v-for="(item, index) in uploadedImages" :key="item.id">
                                                        <a class="d-block flex-grow-1" @click="openImageGallery(index, item)">{{item.originalName}}</a>
                                                        <v-spacer></v-spacer>
                                                        <v-btn fab small primary="transparent" class="elevation-0" icon
                                                            @click="deleteImage(item)">
                                                            <v-icon color="primary">mdi-delete</v-icon>
                                                        </v-btn>
                                                    </li>
                                                </ul>
                                                <div class="file-upload-btn d-flex align-center">
                                                    <file-upload-button class="mb-2" v-model="imagesToUpload"
                                                        :rules="imagesRules" :multiple="true">Browse
                                                    </file-upload-button>
                                                </div>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row px-4 mt-5 mb-5 v-if="taskInfo.linkObligatoriness !== 'Disabled'">
                                            <v-flex xs4 class="d-flex align-center">
                                                <v-subheader class="subtitle-1"
                                                    :class="taskInfo.linkObligatoriness === 'Mandatory' ? 'required' : ''">
                                                    Link
                                                </v-subheader>
                                            </v-flex>
                                            <v-flex xs8 class="d-flex align-center">
                                                <v-text-field
                                                    v-model="taskInfo.attachedLink"
                                                    placeholder="Link attachment"
                                                    :rules="linkAttachmentRules"
                                                    @change="isTouched = true"
                                                ></v-text-field>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row px-4 mt-5 mb-5 v-if="taskInfo.noteObligatoriness !== 'Disabled'">
                                            <v-flex xs4 class="d-flex align-center">
                                                <v-subheader class="subtitle-1"
                                                    :class="taskInfo.noteObligatoriness === 'Mandatory' ? 'required' : ''">
                                                    Notes
                                                </v-subheader>
                                            </v-flex>
                                            <v-flex xs8 class="d-flex align-center">
                                                <v-textarea
                                                    solo
                                                    rows="3"
                                                    v-model="taskInfo.note"
                                                    placeholder="Notes about the task"
                                                    style="position:relative;"
                                                    :rules="noteRules"
                                                    @change="isTouched = true"
                                                ></v-textarea>
                                            </v-flex>
                                        </v-layout>
                                        <v-card-actions>
                                            <v-spacer></v-spacer>
                                            <v-btn class="mx-4" @click="clearForm()">Cancel</v-btn>
                                            <v-btn color="primary" @click="updateTask()" :loading="isUpdating">Save
                                            </v-btn>
                                        </v-card-actions>
                                    </div>
                                    <div v-else class="py-10">
                                        <h3 class="text-center">No evidence required</h3>
                                    </div>
                                    <div>
                                        <h3 class="pb-5 pt-5">Communication</h3>
                                        <v-container>
                                            <v-form ref="commentsForm" class="pb-5">
                                                <v-textarea
                                                    solo
                                                    label="Communication with line manager"
                                                    rows="3"
                                                    :rules="commentForTaskRules"
                                                    v-model="commentForTask"
                                                    class="commentField"
                                                ></v-textarea>
                                                <v-card-actions>
                                                    <v-spacer></v-spacer>
                                                    <v-btn color="grey" @click="addCommentToTask">Post</v-btn>
                                                </v-card-actions>
                                            </v-form>
                                            <div class="comments" v-if="comments.length > 0">
                                                <v-card class="d-flex flex-column grey lighten-3 pa-2 mb-5"
                                                    v-for="c in comments" :key="c.id">
                                                    <div class="d-flex justify-space-between pb-2">
                                                        <div class="font-weight-bold">{{c.employee}}</div>
                                                        <div class="d-flex justify-space-between align-center">
                                                            <div style="width: 150px; font-size: 12px; text-align: right;">
                                                                {{Date.getDateAndTime(c.date)}}
                                                            </div>
                                                            <!-- <app-due-date-circle :date="c.date" :showCircle="false"
                                                                                 :width="100"></app-due-date-circle> -->
                                                            <div class="pl-5" v-if="c.employeeId === getUserId">
                                                                <v-icon @click="editModeActivate(c.id, c.text)">$edit
                                                                </v-icon>
                                                                <v-icon @click="deleteComment(c.id)">$delete</v-icon>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-if="commentEditMode === c.id">
                                                        <v-form ref="editCommentsForm" class="pb-5">
                                                            <v-textarea
                                                                solo
                                                                rows="2"
                                                                :rules="commentForTaskRules"
                                                                v-model="editedCommentForTask"
                                                                class="commentField"
                                                            ></v-textarea>
                                                            <v-card-actions>
                                                                <v-spacer></v-spacer>
                                                                <v-btn color="grey lighten-2"
                                                                    @click="commentEditMode = null">Cancel
                                                                </v-btn>
                                                                <v-btn color="grey" @click="editComment">Edit</v-btn>
                                                            </v-card-actions>
                                                        </v-form>
                                                    </div>
                                                    <div v-else>
                                                        {{c.text}}
                                                    </div>
                                                </v-card>
                                            </div>
                                            <div class="text-center" v-else>
                                                You dont have any comments yet
                                            </div>
                                        </v-container>
                                    </div>
                                    <div>
                                        <h3 class="pb-5">Activity</h3>
                                        <v-container v-if="history.length > 0">
                                            <div class="d-flex justify-space-between pb-5" v-for="h in history"
                                                :key="h.id">
                                                <div>
                                                    <div>{{h.updatedBy}}</div>
                                                    <div>{{h.activity}}</div>
                                                </div>
                                                <div style="width: 150px; font-size: 12px; text-align: right;">
                                                    {{Date.getDateAndTime(h.date)}}
                                                    <!-- <app-due-date-circle :date="h.date" :showCircle="false"
                                                                         :width="100"></app-due-date-circle> -->
                                                </div>
                                            </div>
                                        </v-container>
                                        <div class="text-center" v-else>
                                            You dont have any activity here
                                        </div>
                                    </div>
                                </v-flex>
                                <v-flex xs5 pl-5>
                                    <div class="mb-8">
                                        <table>
                                            <tbody>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Due Date:</b></td>
                                                <td class="pb-4">{{Date.getFormatDateStr(dueDate)}}</td>
                                            </tr>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Interval:</b></td>
                                                <td class="pb-4">{{taskInfo.interval}}</td>
                                            </tr>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Overdue:</b></td>
                                                <td class="pb-4">{{taskInfo.overdue ? 'Overdue' : 'Not overdue'}}</td>
                                            </tr>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Clinic:</b></td>
                                                <td class="pb-4">{{taskInfo.clinic}}</td>
                                            </tr>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Sub-unit:</b></td>
                                                <td class="pb-4">{{taskInfo.surgery}}</td>
                                            </tr>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Assignee:</b></td>
                                                <td class="pb-4">
                                                    {{taskInfo.assignee}}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="pr-10 pb-4"><b>Employee:</b></td>
                                                <td class="pb-4">{{taskInfo.linkedTo}}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="mb-4" v-if="taskInfo.description.length">
                                        <div class="mb-2">
                                            <b>Description</b>
                                        </div>
                                        <div class="task-view__description">
                                            <p>
                                                {{taskInfo.description}}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="mb-4" v-if="attachmentList.length > 0 || taskInfo.linkAttachment">
                                        <div class="mb-2">
                                            <b>Attachments</b>
                                        </div>
                                        <ul style="list-style-type: none">
                                            <li
                                                v-for="(file, index) in attachmentList"
                                                :key="index"
                                                class="mb-3"
                                                @click="openImageGallery(index, file)"
                                            >
                                                <a href="#">Attachment {{index + 1}}</a>
                                            </li>
                                            <li>
                                                <a :href="taskInfo.linkAttachment" target="_blank">{{taskInfo.linkAttachment}}</a>
                                            </li>
                                        </ul>
                                    </div>
                                </v-flex>
                            </v-layout>
                        </v-form>
                        <v-row px-4 fluid
                            class="grey lighten-2 align-center px-5 justify-space-between task-view_footer">
                            <v-flex xs-7 pl5 class="justify-center" style="flex-basis: 46%">
                                <div v-if="isSuperAppraisal && taskInfo.overdue && taskInfo.status === 'Pending' && punishedList.length > 0" class="d-flex justify-center align-center">
                                    <v-btn text @click="openPunishedModal"><v-icon left>$overdue</v-icon>Overdue Penalty</v-btn>
                                </div>
                                <div class="appraisalStatusContainer" v-if="currentStatus === 'Done' && !isUpdating">
                                    <div class="appraisalStatusContainer-title">
                                        Appraisal
                                    </div>
                                    <div class="appraisalStatusContainer-tools">
                                        <v-btn-toggle
                                            v-model="activeAppraisal"
                                            dense
                                            background-color="transparent"
                                            borderless
                                            :loading="true"
                                        >
                                            <v-btn v-for="status in appraisalStatus" :key="status.id"
                                                :value="status.title" x-large icon rounded class="btn-rounded"
                                                @click="showAppraisalCommentPopup(status)"
                                            >
                                                <v-icon x-large>{{ status.icon }}</v-icon>
                                            </v-btn>
                                        </v-btn-toggle>
                                    </div>
                                </div>
                            </v-flex>
                            <v-flex xs-5 pl5>
                                <v-layout row class="py-3 justify-end">
                                    <v-flex xs3 class="d-flex align-center">
                                        <div class="statusWarning" v-if="completeAsAssignee">"Done" will be attributed to Assignee</div>
                                        <v-subheader class="subtitle-1">
                                            <b>Status</b>
                                        </v-subheader>
                                    </v-flex>
                                    <v-flex xs8 class="d-flex align-center">
                                        <div class="status-btns">
                                            <v-btn :value="status.text" v-for="status in statuses" :key="status.id"
                                                class="primary lighten-1 elevation-0"
                                                :class="{'v-btn--active': currentStatus === status.text}"
                                                @click="changeTaskHandler(status.text, $event)"
                                                :disabled="status.disabled || isStatusChanging && statusToChange !== status.text"
                                                :loading="isStatusChanging && statusToChange === status.text">
                                                {{ status.text }}
                                            </v-btn>
                                        </div>
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                        </v-row>
                        <div class="appraisalStatusContainer-comment" v-if="isAppraisalCommentShown">
                            <v-form ref="appraisalCommentForm">
                                <v-text-field
                                    v-model="appraisalComment"
                                    placeholder="Comment"
                                    :rules="appraisalCommentRules"
                                    style="width: 100%; padding-bottom: 20px;"
                                    solo
                                    rows="3"
                                ></v-text-field>
                                <div class="d-flex justify-lg-space-between">
                                    <v-btn color="primary" @click="changeAppraisalTask">Apply</v-btn>
                                    <v-btn color="grey" @click="isAppraisalCommentShown = false">Cancel</v-btn>
                                </div>
                            </v-form>
                        </div>
                    </v-container>
                </v-fade-transition>
                <v-skeleton-loader
                    v-if="isDataLoading"
                    class="mx-auto pa-5"
                    type="article, article"
                >
                </v-skeleton-loader>
            </v-card>
        </v-dialog>
        <doc-view
            :isOpen="docViewOpen"
            :file-value="fileWatchData"
            :carouselIndex="carouselIndex"
            :uploadedImages="uploadedImages.filter(item => isImage.includes(item.extension))"
            @closeModal="docViewOpen = false"
            @setActiveImg="setActiveImg(index)"
            @deleteImage="deleteImage(item)"
        >
        </doc-view>
        <v-dialog
            v-model="overdueModal"
            width="1000"
        >
            <v-card>
                <v-card-title class="headline grey lighten-4 justify-space-between">
                    <v-toolbar-title class="font-weight-regular">
                        Employees who worked during the task span
                    </v-toolbar-title>
                    <v-btn icon @click="overdueModal = false">
                        <v-icon>$close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="md-3">
                                <strong class="body-1 font-weight-bold">Employee</strong>
                            </v-col>
                            <v-col cols="md-3">
                                <strong class="body-1 font-weight-bold">Penalised</strong>
                            </v-col>
                        </v-row>
                        <v-row v-for="personData in punishedList" :key="personData.id">
                            <v-col cols="md-3">
                                <div class="body-1">
                                    {{personData.fullName}}
                                    <span v-if="personData.isSupervisor">(watch)</span>
                                </div>
                            </v-col>
                            <v-col cols="md-3">
                                <v-radio-group v-model="personData.punished" @change="changePunished(personData)" row class="my-0 py-0" hide-details>
                                    <v-radio label="Yes" :value="true" class="mr-10"/>
                                    <v-radio label="No" :value="false"/>
                                </v-radio-group>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="px-5 py-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="putPunishedEmployees"
                        :loading="updatePunishedLoading"
                    >
                        Save changes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import { APPRAISALS, APPRAISALS_VALUES } from '@/common/constants'
    // import AppDueDateCircle from '@/components/AppDueDateCircle'
    import fileUploadButton from '@/components/FileUploadButton'
    import moment from 'moment'
    import INTERVALS from '@/constatns/intervalTypes'
    import DocView from './components/DocView'

    const getScrollableContentHeight = () => {
        let height = window.innerHeight - 72 - 64 - 100
        if (height > 650) height = 650
        return `${height}px`
    }

    export default {
        props: ['isOpen', 'taskId', 'closeTaskViewModal', 'completeAsAssignee'],
        name: 'EmployeeTaskView',
        data: () => ({
            isTouched: false,
            datePickerStatus: false,
            taskInfo: {
                attachments: [],
                note: '',
                attachedLink: '',
                linkAttachment: '',
                isComplete: false,
                images: [],
                supervisorIds: []
            },
            renewableTaskDueDate: null,
            userRenewableTaskDueDate: null,
            galleryDialog: false,
            uploadedImages: [],
            employees: [
                {
                    id: null,
                    value: 'none'
                }
            ],
            assigneeRules: [
                val => !!val || 'Assignee should be chosen',
                val => (val && (val.id !== null)) || 'Assignee should be chosen'
            ],
            assignee: null,
            imagesToUpload: [],
            imagesToDelete: [],
            carouselIndex: 0,
            isUpdating: false,
            isDataLoading: true,
            statusToChange: '',
            currentStatus: '',
            isStatusChanging: false,
            history: [],
            commentForTaskRules: [
                value => !!value || 'Required.'
            ],
            commentForTask: '',
            commentEditMode: null,
            editedCommentForTask: '',
            scrollableContentHeight: getScrollableContentHeight(),
            appraisalComment: '',
            isAppraisalCommentShown: false,
            activeAppraisal: null,
            fileWatchData: null,
            docViewOpen: false,
            overdueModal: false,
            punishedList: [],
            punishedListChanged: [],
            updatePunishedLoading: false
        }),
        computed: {
            ...mapGetters(['getUserClinicPermissions', 'getAppraisalStatus', 'getComments', 'getUserId']),
            height: function () {
                const h = window.innerHeight - 72 - 64 - 100
                return h + 'px'
            },
            isImage () {
                return ['.jpeg', '.jpg', '.png']
            },
            comments: function () {
                return this.getComments
            },
            appraisalStatus: function () {
                return APPRAISALS.filter(item => item.display)
            },
            imagesRules: function () {
                const rules = []
                if (this.uploadedImages.length > 0) {
                    return rules
                }
                if (this.taskInfo.imageObligatoriness === 'Mandatory') {
                    const rule = val => val.length > 0 || 'Images is required'
                    rules.push(rule)
                }
                return rules
            },
            renewableDateRules: function () {
                const rules = []
                if (this.taskInfo.interval === 'Renewable' || this.isHrRenewable) {
                    const rule = val => !!(val || this.getRenewableTaskDueDate()) || 'Incorrect date'
                    const rule2 = val => !Date.checkIsPastDate(val || this.getRenewableTaskDueDate()) || 'Date must be today or greater!'
                    const rule3 = () => {
                        return !(moment(this.userRenewableTaskDueDate || this.getRenewableTaskDueDate()).diff(moment(this.taskInfo.dueDate.substr(0, 10)), 'days') <= 0) || 'Date must be greater then current Due Date!'
                    }
                    rules.push(rule)
                    rules.push(rule2)
                    rules.push(rule3)
                }
                return rules
            },
            noteRules: function () {
                const rules = []
                if (this.taskInfo.noteObligatoriness === 'Mandatory') {
                    const rule = val => !!val || 'Notes is required'
                    rules.push(rule)
                }
                if (this.taskInfo.noteObligatoriness === 'Optional') {
                    const rule =
                        val => {
                            if (val) {
                                return (val.length >= 0 && val.length <= 2000) || 'Comment must be less then 2000 characters'
                            } else {
                                return true
                            }
                        }
                    rules.push(rule)
                }
                return rules
            },
            linkAttachmentRules: function () {
                const rules = []
                if (this.taskInfo.linkObligatoriness === 'Mandatory') {
                    const rule = val => !!val || 'Link is required'
                    rules.push(rule)
                }
                return rules
            },
            appraisalCommentRules: function () {
                const rules = [
                    val => !!val || 'Comment is required',
                    val => val.length > 3 || 'Comment should be more then 3 letters'
                ]
                return rules
            },
            statuses: function () {
                return [
                    {
                        id: 1,
                        text: 'Pending',
                        disabled: false
                    },
                    {
                        id: 2,
                        text: 'Done',
                        disabled: this.taskInfo.overdue ? this.taskInfo.overdue : false
                    },
                    {
                        id: 3,
                        text: 'Canceled',
                        disabled: !this.taskInfo.metadata.isSupervisor
                    }
                ]
            },
            dueDate () {
                if (this.taskInfo.dueDate) {
                    return this.taskInfo.dueDate.substr(0, 10)
                } else {
                    return ''
                }
            },
            attachmentList () {
                return this.taskInfo.attachments
            },
            showEvidenceBlock () {
                if (this.taskInfo.interval === 'Renewable' && this.taskInfo.renewableTaskDueDate) {
                    return true
                }
                const { imageObligatoriness, linkObligatoriness, noteObligatoriness } = this.taskInfo
                return !(imageObligatoriness === 'Disabled' &&
                    linkObligatoriness === 'Disabled' &&
                    noteObligatoriness === 'Disabled')
            },
            isHrRenewable () {
                return this.taskInfo.interval === INTERVALS.HR && this.taskInfo.isHrRenewable
            },
            showRenewableDate () {
                return this.isHrRenewable || (this.taskInfo.interval === INTERVALS.RENEWABLE && this.taskInfo.renewableTaskDueDate)
            },
            isSupervisorPermission () {
                return this.$store.getters.getAllUserPermissions.includes(167)
            },
            userPermissions () {
                return this.$store.getters.getUserPermissions
            },
            isSuperAppraisal () {
                const hasSuperAppraisalForClinic = this.userPermissions.clinic.filter(item => item.clinicId === this.taskInfo.clinicId && item.id === 167).length > 0

                if (hasSuperAppraisalForClinic) {
                  return hasSuperAppraisalForClinic
                }

                const currentGroupId = this.$store.getters.getUserActiveGroup
                const hasSuperAppraisalForGroup = this.userPermissions.groupAdmin.filter(item => item.groupId === currentGroupId && item.id === 167).length > 0

                return hasSuperAppraisalForGroup
            },
            computedRenewDateFormatted () {
                return Date.getFormatDateStr(this.userRenewableTaskDueDate)
            }
        },
        methods: {
            ...mapActions([
                'showAlert',
                'fetchEmployeeTaskById',
                'updatePunishedEmployees',
                'updateEmployeeTask',
                'uploadEmployeeAttachment',
                'changeTaskStatus',
                'setAppraisalStatus',
                'fetchHistoryTask',
                'setCommentToTask',
                'deleteTaskComment']),
            getRenewableTaskDueDate () {
                let renewableTaskDueDate
                if (this.userRenewableTaskDueDate && moment(this.userRenewableTaskDueDate).diff(moment(this.taskInfo.dueDate), 'days') > 0) {
                    renewableTaskDueDate = this.userRenewableTaskDueDate
                } else {
                    if ((this.userRenewableTaskDueDate && !this.renewableTaskDueDate) ||
                        ((this.userRenewableTaskDueDate && this.renewableTaskDueDate) && (moment(this.userRenewableTaskDueDate).diff(moment(this.renewableTaskDueDate), 'days') > 0))
                    ) {
                        renewableTaskDueDate = this.userRenewableTaskDueDate
                    } else {
                        renewableTaskDueDate = this.renewableTaskDueDate
                    }
                }
                return renewableTaskDueDate
            },
            resizeHandler () {
                this.scrollableContentHeight = getScrollableContentHeight()
            },
            openImageGallery (startImageIndex, item) {
                this.carouselIndex = startImageIndex
                this.docViewOpen = true
                this.fileWatchData = item
            },
            closeImageGallery () {
                this.galleryDialog = false
                this.carouselIndex = 0
            },
            setActiveImg (index) {
                this.carouselIndex = index
            },
            clearForm () {
                this.$refs.taskEditForm.reset()
                this.imagesToUpload = []
            },
            closeModal () {
                if (this.$refs.taskEditForm) {
                    this.$refs.taskEditForm.reset()
                    this.$refs.taskEditForm.resetValidation()
                }
                this.appraisalComment = ''
                this.closeTaskViewModal()
                this.isDataLoading = true
                this.taskInfo = {}
                this.commentForTask = ''
                this.renewableTaskDueDate = null
            },
            async deleteImage (image) {
                if (await this.$root.$confirmDialog.open(image.originalName)) {
                    this.taskInfo.images = this.taskInfo.images.filter(img => img.link !== image.link)
                    this.uploadedImages = this.uploadedImages.filter(img => img.link !== image.link)
                    this.imagesToDelete.push(image.link)
                    if (this.uploadedImages.length === 0) {
                        this.closeImageGallery()
                    }
                }
            },
            async updateTask (isChangeStatus) {
                if (!this.isTouched) {
                    return
                }
                const data = this.createFormData(this.taskInfo, isChangeStatus)
                if (this.taskInfo.interval === INTERVALS.RENEWABLE || this.isHrRenewable) {
                    if ((this.renewableTaskDueDate && this.userRenewableTaskDueDate) ||
                        (this.renewableTaskDueDate && isChangeStatus)) {
                        if (this.renewableTaskDueDate && !this.userRenewableTaskDueDate) {
                            if (moment(this.renewableTaskDueDate).diff(moment(this.taskInfo.dueDate.substr(0, 10)), 'days') <= 0) {
                                return false
                            }
                        } else {
                            if (!this.$refs.taskEditForm.validate('taskDueDate')) return
                        }
                    }
                }
                this.isUpdating = true

                if (data.fileFormData) {
                    try {
                        await this.uploadEmployeeAttachment(data)
                        this.imagesToUpload = []
                    } catch (e) {
                    }
                }
                await this.updateEmployeeTask(data).then(() => {
                    if (!isChangeStatus) {
                        this.showAlert({
                            text: 'Saved!',
                            type: 'success'
                        })
                    }
                }).catch((error) => {
                    if (isChangeStatus) {
                        this.showAlert({
                            text: error,
                            type: 'error'
                        })
                    }
                })
                return this.getEmployeeTaskInfo().then(() => {
                    this.isUpdating = false
                    return true
                })
            },
            evidenceValidation () {
                if ((this.taskInfo.imageObligatoriness === 'Mandatory' && !this.imagesToUpload.length) && !this.uploadedImages.length) {
                    this.showAlert({
                        text: 'Image is required',
                        type: 'error'
                    })
                    return false
                }
                if (this.taskInfo.linkObligatoriness === 'Mandatory' && this.taskInfo.attachedLink === '') {
                    this.showAlert({
                        text: 'Link is required',
                        type: 'error'
                    })
                    return false
                }
                if (this.taskInfo.noteObligatoriness === 'Mandatory' && this.taskInfo.note === '') {
                    this.showAlert({
                        text: 'Note is required',
                        type: 'error'
                    })
                    return false
                }
                return true
            },
            async changeTaskHandler (status, event) {
                if (status === 'Done') {
                    if (!this.evidenceValidation() ||
                        !this.$refs.taskEditForm.validate() ||
                        status === this.currentStatus ||
                        this.taskInfo.overdue) return
                    if (this.isHrRenewable) {
                        const renewableTaskDueDate = this.getRenewableTaskDueDate()
                        const confirmModalOptions = {
                            text: `${this.taskInfo.linkedTo} will be added to the template "${this.taskInfo.title}" with a renew date ${Date.formatDate([renewableTaskDueDate])}`,
                            agreeBtnText: 'Proceed',
                            type: 'info'
                        }
                        const confirm = await this.$root.$confirmDialog.open('', confirmModalOptions)
                        if (!confirm) {
                            return false
                        }
                    }
                }

                if (status === 'Pending' && this.currentStatus === 'Done' && !this.taskInfo.metadata.isSupervisor) {
                    this.showAlert({
                        text: 'Only supervisor can change status to Pending!',
                        type: 'info'
                    })
                    this.statusToChange = ''
                    this.isStatusChanging = false
                    return
                }
                const updateTaskStatus = await this.updateTask(true)

                if (!updateTaskStatus && this.isTouched) {
                    this.showAlert({
                        text: 'Please fill all required fields!',
                        type: 'warning'
                    })
                    this.currentStatus = this.taskInfo.status
                    return
                }
                this.$refs.taskEditForm.resetValidation()
                this.statusToChange = status
                this.isStatusChanging = true
                const data = {
                    id: this.taskId,
                    clinicId: this.$store.getters.getUserActiveClinic,
                    status,
                    completeAsAssignee: this.completeAsAssignee
                }

                await this.changeTaskStatus(data).then(() => {
                    this.showAlert({
                        text: 'Status successfully changed to ' + status,
                        type: 'success'
                    })
                    this.currentStatus = status
                }).catch(error => {
                    let errorText = ''
                    if (error.response.data && error.response.data.errors) {
                        for (const errorKey in error.response.data.errors) {
                            if (Object.prototype.hasOwnProperty.call(error.response.data.errors, errorKey)) {
                                errorText += error.response.data.errors[errorKey] + '\n'
                            }
                        }
                    } else {
                        errorText += error.response.data.error
                    }
                    if (status === 'Done') {
                        this.showAlert({
                            text: errorText || 'To change status for Done fill all required fields and press Save',
                            type: 'error'
                        })
                    }
                    this.$refs.taskEditForm.validate()
                    this.currentStatus = this.taskInfo.status
                })
                await this.getEmployeeTaskInfo()
                setTimeout(() => {
                    this.statusToChange = ''
                    this.isStatusChanging = false
                }, 300)
                return false
            },
            createFormData (taskInfo, isChangeStatus) {
                const isRenewable = this.taskInfo.interval === INTERVALS.RENEWABLE
                const renewableTaskDueDate = isRenewable || this.isHrRenewable ? this.getRenewableTaskDueDate() : null
                const data = {
                    id: taskInfo.id,
                    clinicId: this.$store.getters.getUserActiveClinic,
                    employeeId: taskInfo.assignee,
                    status: taskInfo.status,
                    renewableTaskDueDate
                }
                if (this.isHrRenewable) {
                    data.supervisorIds = taskInfo.supervisorIds
                }
                if (this.taskInfo.linkObligatoriness !== 'Disabled') {
                    data.link = taskInfo.attachedLink
                }
                if (this.taskInfo.noteObligatoriness !== 'Disabled') {
                    data.note = taskInfo.note ? taskInfo.note.trim() : ''
                }
                let fileFormData = null
                if (this.imagesToUpload.length !== 0 || this.imagesToDelete.length !== 0) {
                    fileFormData = new FormData()
                    fileFormData.append('id', data.id)
                    fileFormData.append('clinicId', data.clinicId)
                }
                if (this.imagesToUpload.length > 0) {
                    this.imagesToUpload.forEach(img => {
                        fileFormData.append('images', img, img.name)
                    })
                }
                if (this.imagesToDelete.length > 0) {
                    this.imagesToDelete.forEach((img, index) => {
                        fileFormData.append('imagesToDelete', img)
                    })
                }

                return {
                    taskInfo: data,
                    fileFormData,
                    imagesToUpload: this.imagesToUpload.length !== 0
                }
            },
            getEmployeeTaskInfo () {
                return this.fetchEmployeeTaskById(this.taskId).then((data) => {
                    this.taskInfo = { ...this.taskInfo, ...data }
                    const { renewableTaskDueDate, interval, status, metadata, images } = this.taskInfo
                    this.activeAppraisal = data.appraisalStatus
                    if (renewableTaskDueDate && renewableTaskDueDate.dueDate) {
                        this.renewableTaskDueDate = renewableTaskDueDate.dueDate.substr(0, 10)
                    }

                    if (interval === INTERVALS.RENEWABLE || this.isHrRenewable) {
                        if ((renewableTaskDueDate && !renewableTaskDueDate.dueDate)) {
                            this.taskInfo.renewableTaskDueDate = null
                        }
                        if (status === 'Done' && renewableTaskDueDate && renewableTaskDueDate.dueDate) {
                            this.userRenewableTaskDueDate = renewableTaskDueDate.dueDate.substr(0, 10)
                        }
                    }

                    this.currentStatus = status
                    if (metadata && metadata.employees) {
                        this.employees = this.employees.concat(metadata.employees)
                    }
                    this.uploadedImages = images
                    this.isDataLoading = false
                    this.addPunishedEmployees()
                    this.fetchHistoryTask(this.taskId).then(res => {
                        this.history = res.items
                    })
                })
            },
            showAppraisalCommentPopup (status) {
                if (status.title === 'Correct') return this.changeAppraisalTask(status.title)
                this.isAppraisalCommentShown = true
            },
            changeAppraisalTask (status) {
                if (!this.taskInfo.metadata.isSupervisor && !this.isSupervisorPermission) {
                    return this.showAlert({
                        text: 'You have no right to edit this task',
                        type: 'error'
                    })
                }
                if (!this.appraisalComment.length && !status) {
                    return this.showAlert({
                        text: 'Comment is required',
                        type: 'error'
                    })
                }
                if (!this.activeAppraisal && this.getAppraisalStatus) {
                    return this.showAlert({
                        text: 'Appraisal must be selected',
                        type: 'error'
                    })
                }
                const appraisalStatus = typeof status === 'string' ? status : this.activeAppraisal

                if (appraisalStatus === APPRAISALS_VALUES.INCORRECT) {
                    if (!this.$refs.appraisalCommentForm.validate()) {
                        return
                    }
                }
                // appraisalCommentForm
                this.setAppraisalStatus({
                    id: this.taskId,
                    clinicId: this.$store.getters.getUserActiveClinic,
                    groupId: this.$store.getters.getUserActiveGroup,
                    appraisalStatus,
                    comment: this.appraisalComment
                }).then(() => {
                    this.showAlert({
                        text: 'Saved!',
                        type: 'success'
                    })
                    this.isAppraisalCommentShown = false
                    this.activeAppraisal = ''
                    this.$store.dispatch('removeSpotCheckItem', this.taskId)
                    this.getEmployeeTaskInfo()
                    this.appraisalComment = ''
                }).catch((e) => {
                    this.showAlert({
                        text: e.response.data.errors[0],
                        type: 'error'
                    })
                })
            },
            addCommentToTask () {
                if (this.$refs.commentsForm.validate()) {
                    const params = {
                        taskId: this.taskId,
                        clinicId: this.$store.getters.getUserActiveClinic,
                        groupId: this.$store.getters.getUserActiveGroup,
                        text: this.commentForTask
                    }
                    this.setCommentToTask(params).then(() => {
                        this.commentForTask = ''
                        this.getEmployeeTaskInfo()
                        this.$refs.commentsForm.resetValidation()
                    })
                }
            },
            deleteComment (commentId) {
                this.deleteTaskComment(commentId).then(() => {
                    this.getEmployeeTaskInfo()
                })
            },
            editModeActivate (id, text) {
                this.commentEditMode = id
                this.editedCommentForTask = text
            },
            editComment () {
                if (this.$refs.editCommentsForm[0].validate()) {
                    const params = {
                        taskId: this.taskId,
                        clinicId: this.$store.getters.getUserActiveClinic,
                        groupId: this.$store.getters.getUserActiveGroup,
                        text: this.editedCommentForTask,
                        commentId: this.commentEditMode
                    }
                    this.setCommentToTask(params).then(() => {
                        this.editedCommentForTask = ''
                        this.commentEditMode = null
                        this.getEmployeeTaskInfo()
                    })
                }
            },
            addPunishedEmployees () {
                this.punishedList = this.taskInfo.punishedEmployees
                this.punishedListChanged = []
            },
            openPunishedModal () {
                this.updatePunishedLoading = false
                this.addPunishedEmployees()
                this.overdueModal = true
            },
            changePunished (newItem) {
                if (this.punishedListChanged.length === 0) return this.punishedListChanged.push(newItem)
                const checkExistIndex = this.punishedListChanged.findIndex((item) => item.id === newItem.id)
                if (checkExistIndex === -1) {
                    this.punishedListChanged.push(newItem)
                } else {
                    this.punishedListChanged[checkExistIndex] = newItem
                }
            },
            async putPunishedEmployees () {
                if (this.punishedListChanged.length === 0) return
                const queryData = {}
                const employeeData = []
                this.punishedListChanged.forEach((item) => {
                    employeeData.push({
                        employeeId: item.id,
                        punished: item.punished,
                        isSupervisor: item.isSupervisor
                    })
                })
                queryData.taskId = this.taskId
                queryData.clinicId = this.$store.getters.getUserActiveClinic
                queryData.groupId = this.$store.getters.getUserActiveGroup
                queryData.employees = employeeData
                try {
                    this.updatePunishedLoading = true
                    await this.updatePunishedEmployees(queryData)
                    await this.getEmployeeTaskInfo()
                    this.updatePunishedLoading = false
                    this.overdueModal = false
                } catch (e) {
                    this.updatePunishedLoading = true
                }
            }
        },
        watch: {
            async isOpen () {
                if (!this.isOpen) {
                    this.datePickerStatus = false
                    return
                }
                this.getEmployeeTaskInfo()
            },
            imagesToUpload () {
                this.isTouched = true
            },
            uploadedImages () {
                this.isTouched = true
            }
        },
        components: {
            // AppDueDateCircle,
            fileUploadButton,
            DocView
        },
        created () {
            window.addEventListener('resize', this.resizeHandler)
        },
        beforeDestroy () {
            window.removeEventListener('resize', this.resizeHandler)
        }
    }
</script>

<style lang="scss">
    .border-right-separator {
        border-right: 1px solid #e0e0e0;
    }

    .statusWarning {
        font-size: 12px;
        color: red;
        position: absolute;
        top: 3px;
        line-height: 1;
        width: 200px;
        text-align: center;
        right: 70px;
    }

    .opacity-4 {
        opacity: .4;
    }

    .status-btns {
        button.v-btn {
            border-radius: 0;
            border-color: rgba(0, 0, 0, 0.12) !important;
            border-left-width: 0;
            &:first-child {
                border-radius: 4px 0 0 4px;
            }

            &:last-child {
                border-radius: 0 4px 4px 0;
            }
        }

        .v-btn:not(.v-btn--text):not(.v-btn--outlined).v-btn--active {
            color: #fff;
            background-color: #6c9d39 !important;
            pointer-events: none;
            cursor: default;

            &::before {
                opacity: 0;
            }
        }

        /* .v-btn--active {
             color: #fff;
             background-color: #6c9d39 !important;
             pointer-events: none;
             cursor: default;

             &::before {
                 opacity: 0;
             }
         }*/
    }

    .v-text-field.v-text-field--enclosed .v-text-field__details {
        margin-bottom: 0 !important;
    }

    .appraisalStatusContainer {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        &-title {
            font-weight: bolder;
        }

        &-tools {
            padding-left: 20px;

            .theme--light.v-btn-toggle:not(.v-btn-toggle--group) .v-btn.v-btn.btn-rounded {
                border-radius: 50px;
            }
        }

        &-icon {
            width: 50px;
            height: 50px;
        }
        &-comment {
            position: absolute;
            background: #e0e0e0;
            bottom: 90px;
            left: 200px;
            padding: 15px;
            border-radius: 5px;
            width: 300px;

            .v-text-field__details {
                bottom: 60px !important;
            }
        }
    }

    .modalOverflowHidden {
        overflow: hidden;
    }

    .commentField .v-input__control .v-text-field__details {
        position: relative;
    }

    .task-view__description {
        max-height: 300px;
        font-size: 14px;
        overflow: auto;
    }

    .task-view {
        /* &_container {
          height: 850px;
          overflow: auto;
          position: relative;
        } */
        &_form {
            padding-bottom: 52px;
        }

        &_content {
            height: 650px;
            min-height: 250px;
            overflow: auto;
        }

        &_footer {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            margin: 0;
        }
    }
    .v-image__image--cover {
        background-size: contain;
    }
</style>
